/**
 * Centralized adapter for GraphQL query parameter naming.
 *
 * Some backend endpoints use non-standard parameter names for pagination.
 * Instead of scattering rename logic across view configs or individual components,
 * this module provides a single source of truth.
 *
 * Standard convention: { page, perPage }
 * Exceptions are listed in ENDPOINT_PARAMS_MAP below.
 */

type ParamRenameMap = Record<string, string>
type ParamTransformMap = Record<string, (value: any) => any>

const ENDPOINT_PARAMS_MAP: Record<string, ParamRenameMap> = {
  changeRequests: {perPage: 'limit', uuids: 'uuid'},
  getReport: {perPage: 'per_page'},
}

// Value transformations applied AFTER renaming
const ENDPOINT_VALUE_TRANSFORMS: Record<string, ParamTransformMap> = {
  changeRequests: {
    uuid: (value: any) => Array.isArray(value) ? value[0] : value,
  },
}

export function adaptRequestParams(
  endpointName: string,
  params?: Record<string, any>,
): Record<string, any> | undefined {
  if (!params) return params

  const renameMap = ENDPOINT_PARAMS_MAP[endpointName]
  const transforms = ENDPOINT_VALUE_TRANSFORMS[endpointName]
  if (!renameMap && !transforms) return params

  const adapted = {...params}

  if (renameMap) {
    for (const [from, to] of Object.entries(renameMap)) {
      if (from in adapted) {
        adapted[to] = adapted[from]
        delete adapted[from]
      }
    }
  }

  if (transforms) {
    for (const [key, transform] of Object.entries(transforms)) {
      if (key in adapted) {
        adapted[key] = transform(adapted[key])
      }
    }
  }

  return adapted
}
